import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  isExternalHref,
  normalizeExternalUrl,
} from "../Helper/urls";
import { fetchPlatformAvailability } from "./platformAvailabilityApi";
import PlatformGrid from "./PlatformGrid";
import "./PlatformLaunchPage.css";

/**
 * Hero CTA — same rules as Shop/referral Hero Text: both text and URL required.
 */
function HeroButton({ button }) {
  if (!button) return null;
  const text = (button.text || "").trim();
  const url = (button.url || "").trim();
  if (!text || !url) return null;

  const className = "btn btn-danger platform-launch__cta";
  if (isExternalHref(url)) {
    return (
      <a
        className={className}
        href={normalizeExternalUrl(url)}
        target={button.new_tab ? "_blank" : undefined}
        rel={button.new_tab ? "noopener noreferrer" : undefined}
      >
        {text}
      </a>
    );
  }

  if (button.new_tab) {
    return (
      <a
        className={className}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        {text}
      </a>
    );
  }

  return (
    <Link to={url} className={className}>
      {text}
    </Link>
  );
}

/**
 * A-009A / A-009A.1 — Launch Center. Renders platformAvailabilityPayload only.
 * Hero fields are CMS-managed; blank means intentionally blank.
 */
function PlatformLaunchPage(props) {
  const orgSlug =
    (props.match && props.match.params && props.match.params.slug) || "";
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchPlatformAvailability({
          organizationSlug: orgSlug || undefined,
        });
        if (!cancelled) setPayload(data);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "Unable to load platforms.");
          setPayload(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgSlug]);

  if (loading) {
    return (
      <div className="platform-launch platform-launch--loading">
        <p>Loading platforms…</p>
      </div>
    );
  }

  if (!payload) {
    return (
      <div className="platform-launch platform-launch--empty">
        {error ? <p className="platform-launch__error">{error}</p> : null}
        <Link className="btn btn-danger" to="/home">
          Home
        </Link>
      </div>
    );
  }

  const entity = payload.entity || {};
  const hero = payload.hero || {};
  const heroTitle = (hero.title || "").trim();
  const heroSubtitle = (hero.subtitle || "").trim();
  const button = hero.button || {};
  const hasButton =
    Boolean((button.text || "").trim()) && Boolean((button.url || "").trim());
  const platforms = payload.platforms || [];
  const documentTitle = heroTitle || entity.name || "Platforms";
  const showHeroBlock = Boolean(heroTitle || heroSubtitle || hasButton || orgSlug);

  return (
    <div className="platform-launch">
      <Helmet>
        <title>{`${documentTitle}${entity.name ? ` | ${entity.name}` : ""}`}</title>
        {heroSubtitle ? <meta name="description" content={heroSubtitle} /> : null}
      </Helmet>

      {showHeroBlock ? (
        <header className="platform-launch__hero">
          <div className="platform-launch__hero-inner">
            {orgSlug && entity.name ? (
              <p className="platform-launch__crumb">
                <Link to={`/organization/${orgSlug}`}>{entity.name}</Link>
                <span aria-hidden="true"> / </span>
                <span>Platforms</span>
              </p>
            ) : null}
            {heroTitle ? (
              <h1 className="platform-launch__brand">{heroTitle}</h1>
            ) : null}
            {heroSubtitle ? (
              <p className="platform-launch__subtitle">{heroSubtitle}</p>
            ) : null}
            {hasButton ? <HeroButton button={button} /> : null}
          </div>
        </header>
      ) : null}

      <section className="platform-launch__section">
        <div className="platform-launch__section-inner">
          <PlatformGrid platforms={platforms} />
        </div>
      </section>
    </div>
  );
}

export default PlatformLaunchPage;
